import { useState, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { NodeAvatar, Breadcrumb, Pill, ConnectButton, EditableTitle, showToast } from "../primitives";
import type { BreadcrumbSegment, HostKind } from "../primitives";
import { agentPersona } from "../../lib/agent-persona";
import { useConnect } from "../../hooks/use-connect";
import { useToolCountForWorkspace } from "../../hooks/use-tool-count";
import type { LibraryNodeDetail } from "../../../lib/types";
import { useUpdateNode, useUpdateWorkspaceName } from "../../../lib/hooks/use-kuvio";
import { useAuth } from "../../../lib/hooks/use-auth";
import { ActionMenu, type ActionMenuItem } from "../shell/ActionMenu";
import { NewFolderModal } from "../scope/NewFolderModal";
import { NewTaskModal } from "../scope/NewTaskModal";
import { MoveToModal } from "../scope/MoveToModal";
import { ArchiveModal } from "../scope/ArchiveModal";
import { DemoteModal } from "./DemoteModal";
import { PromoteModal } from "./PromoteModal";
import { PlanWithAgentModal } from "../scope/PlanWithAgentModal";
import { ManageAccessModal } from "../scope/ManageAccessModal";
import { CockpitScopeChip } from "./CockpitScopeChip";

// RC1-1 — the identity-colour derivation now lives in ONE place
// (lib/agent-persona.ts). This name is kept as a thin re-export so the ~18
// surfaces that import `getAgentPersona` from here keep working while pointing at
// the single source. Prefer importing `agentPersona` from lib/agent-persona.ts.
export const getAgentPersona = agentPersona;

interface CockpitHeaderProps {
  node: LibraryNodeDetail;
}

export function CockpitHeader({ node }: CockpitHeaderProps) {
  const navigate = useNavigate();
  const { user, currentWorkspaceId } = useAuth();
  const isOwner = user?.workspaces.find((w) => w.id === currentWorkspaceId)?.role === "owner";
  const persona = getAgentPersona({ ...node, agent_config: node.agent_config as Record<string, unknown> | null });
  const isActive = node.status === "active";
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const updateNode = useUpdateNode();
  const updateWorkspaceName = useUpdateWorkspaceName();
  const isRoot = !!(node as any).is_root;
  const isFolder = node.type === "folder";
  const [demoteOpen, setDemoteOpen] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const restricted = !!node.access_scope?.allowed_users?.length;
  const connect = useConnect();
  // Sprint 16f — at workspace scale, recommend scoped endpoints over the umbrella.
  const toolCount = useToolCountForWorkspace();

  // Sprint 5w — the primary CTA follows the node's nature: an agent's natural
  // creation is a sub-agent; a folder's is more folders. Lower-frequency actions
  // move into the overflow menu, ordered by frequency (reorg/lifecycle above the
  // deprioritised create/convert actions).
  // Sprint 23.5 — "new (sub-)agent" navigates to the Hire wizard, pre-filled
  // with reports_to = this node when not the root.
  const hireUrl = (parentId: string | null) =>
    `/hire${parentId && !isRoot ? `?parent_id=${encodeURIComponent(parentId)}` : ""}`;

  const primaryAction = isFolder
    ? { label: "New folder", icon: "folder-plus", onClick: () => setNewFolderOpen(true) }
    : {
        label: isRoot ? "New agent" : "New sub-agent",
        icon: "plus",
        onClick: () => navigate(hireUrl(node.id)),
      };

  const actionItems: ActionMenuItem[] = [];
  if (isFolder) {
    // Folder cockpit: folder creation is the primary button. Sub-agent creation
    // leads the overflow, then reorg/lifecycle, then promotion.
    actionItems.push({
      label: "New sub-agent",
      icon: "plus",
      onClick: () => navigate(hireUrl(node.id)),
    });
    if (!isRoot) {
      actionItems.push({ label: "Move to…", icon: "arrows-move", onClick: () => setMoveOpen(true) });
      actionItems.push({ label: "Archive folder", icon: "archive", variant: "danger", onClick: () => setArchiveOpen(true) });
    }
    actionItems.push({ label: "Promote to agent", icon: "robot", onClick: () => setPromoteOpen(true) });
  } else {
    // Sprint 8 — "Plan with this agent" leads the agent overflow: the highest-
    // value action when starting a planning session (composes persona +
    // priorities + activity to hand off to Claude / Cursor).
    actionItems.push({ label: "Plan with this agent", icon: "wand", onClick: () => setPlanOpen(true) });
    // Agent cockpit: sub-agent creation is the primary button. Reorg/lifecycle
    // first; "New folder" is deprioritised below them (the 5w fix), demotion last.
    if (!isRoot) {
      actionItems.push({ label: "Move to…", icon: "arrows-move", onClick: () => setMoveOpen(true) });
      actionItems.push({ label: "Archive agent", icon: "archive", variant: "danger", onClick: () => setArchiveOpen(true) });
    }
    // Sprint 7 — quick task creation from any agent cockpit (tasks need an agent).
    actionItems.push({ label: "New task", icon: "checklist", onClick: () => setNewTaskOpen(true) });
    actionItems.push({ label: "New folder", icon: "folder-plus", onClick: () => setNewFolderOpen(true) });
    if (!isRoot) {
      actionItems.push({ label: "Demote to folder", icon: "folder", onClick: () => setDemoteOpen(true) });
    }
  }
  // Sprint 14c — owner-only "Manage access" on any non-root node.
  if (isOwner && !isRoot) {
    actionItems.push({ label: "Manage access", icon: "lock", onClick: () => setAccessOpen(true) });
  }

  const segments: BreadcrumbSegment[] = (node._breadcrumb ?? []).map((b) => ({
    label: b.name,
    onClick: () => navigate(`/agents/${b.id}`),
  }));
  segments.push({ label: node.name });

  const scopeParts = node.path.split("/").filter(Boolean);
  const scopeSubtitle = scopeParts.length > 1
    ? scopeParts.slice(0, -1).join(" / ")
    : undefined;

  const row: CSSProperties = {
    display: "flex",
    alignItems: "flex-start",
    gap: 16,
    padding: "20px 0",
  };

  const info: CSSProperties = {
    flex: 1,
    minWidth: 0,
  };

  const nameRow: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginTop: 6,
  };

  return (
    <div>
      <Breadcrumb segments={segments} />
      <div style={row}>
        <NodeAvatar type={node.type} persona={persona} size="xl" name={node.name} />
        <div style={info}>
          <div style={nameRow}>
            <EditableTitle
              value={node.name}
              onSave={async (newName) => {
                try {
                  if (isRoot) {
                    await updateWorkspaceName.mutateAsync(newName);
                  } else {
                    await updateNode.mutateAsync({ id: node.id, name: newName });
                  }
                  showToast(`Renamed to '${newName}'`);
                } catch (err) {
                  const msg = err instanceof Error ? err.message : "Something went wrong";
                  showToast(`Couldn't rename: ${msg}`, "error", true);
                  throw err;
                }
              }}
            />
            <Pill variant={isActive ? "teal" : "gray"}>
              {isActive ? "active" : "idle"}
            </Pill>
            {restricted && (
              <span
                title={`Restricted to ${node.access_scope!.allowed_users.length} user${node.access_scope!.allowed_users.length === 1 ? "" : "s"}`}
                style={{ display: "inline-flex", alignItems: "center", color: "var(--color-text-tertiary)", fontSize: 13 }}
              >
                <i className="ti ti-lock" aria-hidden="true" />
              </span>
            )}
            <CockpitScopeChip />
          </div>
          {scopeSubtitle && (
            <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginTop: 2 }}>
              {scopeSubtitle}
            </div>
          )}
          {!isRoot && (
            <DescriptionStrip
              value={node.description}
              placeholder={
                isFolder
                  ? "Add a one-line description — what is this folder for?"
                  : "Add a one-line description for this agent"
              }
              onSave={async (text) => {
                await updateNode.mutateAsync({ id: node.id, description: text });
              }}
            />
          )}
        </div>
        <button
          onClick={primaryAction.onClick}
          style={{
            fontSize: 13,
            padding: "6px 12px",
            display: "flex",
            alignItems: "center",
            gap: 4,
            border: "var(--kuvio-border-default)",
            borderRadius: "var(--border-radius-sm)",
            background: "var(--color-background-primary)",
            color: "var(--color-text-secondary)",
            cursor: "pointer",
            fontFamily: "var(--font-sans)",
            whiteSpace: "nowrap",
          }}
        >
          <i className={`ti ti-${primaryAction.icon}`} style={{ fontSize: 14 }} aria-hidden="true" />
          {primaryAction.label}
        </button>
        {actionItems.length > 0 && <ActionMenu items={actionItems} />}
        <ConnectButton
          agentId={node.id}
          onConnect={(surface) => connect.connectTo(surface as unknown as HostKind, node.id, node.name)}
          hostStates={connect.hostStates}
          lastSeenByHost={connect.lastSeenByHost}
          scale={{ ...toolCount.plan, umbrellaAttached: toolCount.umbrellaAttached }}
        />
      </div>

      <ArchiveModal
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        nodes={[node]}
        onArchived={() => {
          const parent = node._breadcrumb?.length ? node._breadcrumb[node._breadcrumb.length - 1] : null;
          navigate(parent ? `/agents/${parent.id}` : "/");
        }}
      />

      <NewFolderModal
        open={newFolderOpen}
        onClose={() => setNewFolderOpen(false)}
        parentId={node.id}
        parentName={node.name}
        onCreated={(created) => navigate(`/agents/${created.id}`)}
      />

      {!isFolder && (
        <NewTaskModal open={newTaskOpen} onClose={() => setNewTaskOpen(false)} nodeId={node.id} />
      )}

      <MoveToModal
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        sources={[node]}
        onMoved={() => navigate(`/agents/${node.id}`)}
      />

      <DemoteModal open={demoteOpen} onOpenChange={setDemoteOpen} node={node} />
      <PromoteModal open={promoteOpen} onOpenChange={setPromoteOpen} node={node} />

      <ManageAccessModal
        open={accessOpen}
        onClose={() => setAccessOpen(false)}
        name={node.name}
        nodeId={node.id}
        currentAllowedUsers={node.access_scope?.allowed_users ?? null}
      />

      {!isFolder && (
        <PlanWithAgentModal
          open={planOpen}
          onClose={() => setPlanOpen(false)}
          agentId={node.id}
          agentName={node.name}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DescriptionStrip — single-line, inline-editable folder/agent readme (5v).
// Click to edit; Enter or blur saves (empty clears); Esc cancels. The empty
// placeholder only appears on hover/focus so a description-less node stays clean.
// ---------------------------------------------------------------------------

function DescriptionStrip({
  value,
  placeholder,
  onSave,
}: {
  value: string | null;
  placeholder: string;
  onSave: (text: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [hovered, setHovered] = useState(false);
  const [saving, setSaving] = useState(false);

  const current = value?.trim() ?? "";

  async function commit() {
    const next = draft.trim();
    if (next === current) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch (err) {
      showToast(`Couldn't save description: ${err instanceof Error ? err.message : "error"}`, "error", true);
    } finally {
      setSaving(false);
    }
  }

  const base: CSSProperties = {
    fontSize: 13,
    fontStyle: "italic",
    color: "var(--color-text-tertiary)",
    marginTop: 6,
    lineHeight: 1.4,
  };

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        disabled={saving}
        maxLength={200}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          if (e.key === "Escape") { setDraft(current); setEditing(false); }
        }}
        placeholder={placeholder}
        style={{
          ...base,
          fontStyle: "normal",
          width: "100%",
          padding: "2px 0",
          border: "none",
          borderBottom: "0.5px solid var(--color-border-secondary)",
          background: "transparent",
          color: "var(--color-text-secondary)",
          outline: "none",
          fontFamily: "var(--font-sans)",
        }}
        aria-label="Edit description"
      />
    );
  }

  if (!current) {
    return (
      <div
        onClick={() => { setDraft(""); setEditing(true); }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && (setDraft(""), setEditing(true))}
        style={{ ...base, cursor: "pointer", opacity: hovered ? 1 : 0, transition: "opacity 120ms ease", minHeight: 18 }}
      >
        {placeholder}
      </div>
    );
  }

  return (
    <div
      onClick={() => { setDraft(current); setEditing(true); }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && (setDraft(current), setEditing(true))}
      title="Click to edit"
      style={{ ...base, cursor: "pointer" }}
    >
      {current}
    </div>
  );
}
